import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getLeagueRankingForProfessor } from '../../../Api/authApi.js.js';
import { formatPrice } from '../../../functions/price-formater.js';
import { Skeleton, SkeletonCircle } from '../../components/Skeleton';
import './classement.css';

const PALETTE = ['#7AB8E0', '#9B7BE0', '#E0C281', '#7AE0C5', '#E07AAB', '#4ADE9E', '#FF7A78'];

const nameColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
    return PALETTE[h % PALETTE.length];
};

const initials = (name = '') =>
    name.split(/\s+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '?';

const fmtPct = (n) => {
    const v = parseFloat(n) || 0;
    return (v >= 0 ? '+' : '') + v.toFixed(2) + ' %';
};

function ClassementSkeleton() {
    return (
        <>
            <div className="relief prof-classement-podium-wrap">
                <div className="prof-classement-podium-glow" />
                <div className="prof-classement-podium-grid">
                    {[{ place: 2, height: 72 }, { place: 1, height: 92 }, { place: 3, height: 58 }].map(({ place, height }) => (
                        <div key={place} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <SkeletonCircle size={place === 1 ? 56 : 44} />
                            <div style={{ textAlign: 'center', minHeight: place === 1 ? 34 : 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                <Skeleton width={place === 1 ? 60 : 48} height={place === 1 ? 13 : 11} radius={4} />
                                <Skeleton width={32} height={11} radius={4} />
                            </div>
                            <Skeleton width="100%" height={height} radius="10px 10px 0 0" style={{ marginTop: 4 }} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="prof-classement-section-header">
                <Skeleton width={130} height={16} radius={4} />
                <Skeleton width={60} height={11} radius={4} />
            </div>
            <div className="relief" style={{ borderRadius: 18, padding: '4px 14px' }}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} style={{
                        display: 'grid', gridTemplateColumns: '24px 32px 1fr auto auto', gap: 10, alignItems: 'center',
                        borderBottom: i < 5 ? '1px solid var(--border)' : 'none', padding: '10px 0',
                    }}>
                        <Skeleton width={16} height={12} radius={4} />
                        <SkeletonCircle size={32} />
                        <div>
                            <div style={{ marginBottom: 4 }}><Skeleton width={90} height={13} radius={4} /></div>
                            <Skeleton width={60} height={11} radius={4} />
                        </div>
                        <Skeleton width={50} height={11} radius={4} style={{ marginLeft: 'auto' }} />
                        <Skeleton width={40} height={11} radius={4} style={{ marginLeft: 'auto' }} />
                    </div>
                ))}
            </div>
        </>
    );
}

const ClassementProf = () => {
    const [scope, setScope] = useState('general');
    const professeurId = useSelector((state) => state.auth.user?.id);

    const [teamRanking, setTeamRanking] = useState([]);
    const [individualRanking, setIndividualRanking] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | ready | empty | error

    useEffect(() => {
        if (!professeurId) return;
        getLeagueRankingForProfessor(professeurId)
            .then(res => {
                const teams = res.teams || [];
                setTeamRanking(teams);
                setIndividualRanking(res.individualRanking || []);
                setStatus(teams.length ? 'ready' : 'empty');
            })
            .catch(() => setStatus('error'));
    }, [professeurId]);

    const rankedTeams = [...teamRanking].sort(
        (a, b) => (parseFloat(b.total_montant) || 0) - (parseFloat(a.total_montant) || 0)
    );

    const top3Teams = rankedTeams.slice(0, 3);
    const top3Players = individualRanking.slice(0, 3);

    return (
        <div className="prof-classement-page">

            <div style={{ padding: '4px 4px 14px' }}>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.08em', color: 'var(--text-mute)', textTransform: 'uppercase', marginBottom: 6 }}>
                    Suivi de session
                </div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>
                    {scope === 'general' ? 'Top traders' : 'Top équipes'}
                </h1>
            </div>

            <div className="prof-classement-tabs">
                {[
                    { id: 'general', label: 'Général' },
                    { id: 'equipe', label: 'Équipes' },
                ].map(t => (
                    <button
                        key={t.id}
                        onClick={() => setScope(t.id)}
                        className="prof-classement-tab-btn"
                        style={{
                            background: scope === t.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                            color: scope === t.id ? 'var(--text)' : 'var(--text-dim)',
                        }}
                    >{t.label}</button>
                ))}
            </div>

            {status === 'loading' && <ClassementSkeleton />}
            {status === 'empty' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                    Aucun classement disponible pour votre ligue.
                </div>
            )}
            {status === 'error' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--down)', fontSize: 13 }}>
                    Impossible de charger le classement.
                </div>
            )}

            {status === 'ready' && scope === 'general' && (
                <>
                    <div className="relief prof-classement-podium-wrap">
                        <div className="prof-classement-podium-glow" />
                        <div className="prof-classement-podium-grid">
                            <PodiumStep entry={top3Players[1]} place={2} height={72} />
                            <PodiumStep entry={top3Players[0]} place={1} height={92} />
                            <PodiumStep entry={top3Players[2]} place={3} height={58} />
                        </div>
                    </div>

                    <div className="prof-classement-section-header">
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Classement général</h2>
                        <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>{individualRanking.length} traders</span>
                    </div>
                    <div className="relief" style={{ borderRadius: 18, padding: '4px 14px' }}>
                        {individualRanking.map(player => (
                            <PlayerRow key={player.user_id} player={player} />
                        ))}
                    </div>
                </>
            )}

            {status === 'ready' && scope === 'equipe' && (
                <>
                    <div className="relief prof-classement-podium-wrap">
                        <div className="prof-classement-podium-glow" />
                        <div className="prof-classement-podium-grid">
                            <PodiumStep entry={top3Teams[1]} place={2} height={72} teamMode />
                            <PodiumStep entry={top3Teams[0]} place={1} height={92} teamMode />
                            <PodiumStep entry={top3Teams[2]} place={3} height={58} teamMode />
                        </div>
                    </div>

                    <div className="prof-classement-section-header">
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Classement équipes</h2>
                        <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>{rankedTeams.length} équipes</span>
                    </div>
                    <div className="relief" style={{ borderRadius: 18, padding: '4px 14px' }}>
                        {rankedTeams.map((team, index) => (
                            <TeamRow key={team.team_id} team={team} rank={index + 1} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

/* ── Shared components (mêmes que la page classement joueur, sans les blocs "moi/mon équipe") ── */

function NameAvatar({ name = '', size = 32, ring }) {
    const color = nameColor(name);
    return (
        <div style={{
            width: size, height: size, borderRadius: size / 2,
            background: `linear-gradient(180deg, ${color}44 0%, ${color}18 100%)`,
            color,
            border: ring ? `2px solid ${ring}` : '1px solid var(--border)',
            display: 'grid', placeItems: 'center',
            fontWeight: 700, fontSize: Math.round(size * 0.36),
            boxShadow: ring ? `0 0 0 3px ${ring}22` : 'none',
            flexShrink: 0,
            letterSpacing: '0.01em',
        }}>{initials(name)}</div>
    );
}

function PodiumStep({ entry, place, height, teamMode = false }) {
    const colors = { 1: '#E0C281', 2: '#B8C2D6', 3: '#C99373' };
    const c = colors[place];
    const isEmpty = !entry;
    const name = teamMode ? entry?.team_name : entry?.username;
    const pct = teamMode ? parseFloat(entry?.total_montant) : parseFloat(entry?.pct);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            {place === 1 && (
                <svg width="20" height="20" viewBox="0 0 20 20" style={{ marginBottom: -4, opacity: isEmpty ? 0.25 : 1 }}>
                    <path d="M3 6l3 3 4-5 4 5 3-3v9H3z" fill={c} />
                </svg>
            )}
            {isEmpty ? (
                <div style={{
                    width: place === 1 ? 56 : 44, height: place === 1 ? 56 : 44,
                    borderRadius: '50%',
                    border: `2px dashed ${c}44`,
                    flexShrink: 0,
                }} />
            ) : (
                <NameAvatar name={name} size={place === 1 ? 56 : 44} ring={c} />
            )}
            <div style={{ textAlign: 'center', minHeight: place === 1 ? 34 : 30 }}>
                {!isEmpty && (
                    <>
                        <div style={{
                            fontSize: place === 1 ? 13 : 11, fontWeight: 600, lineHeight: 1.2,
                            maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {name}
                        </div>
                        <div className="num" style={{ fontSize: 11, color: (pct || 0) >= 0 ? 'var(--up)' : 'var(--down)', marginTop: 1 }}>
                            {fmtPct(pct || 0)}
                        </div>
                    </>
                )}
            </div>
            <div style={{
                width: '100%', height,
                background: isEmpty ? `rgba(255,255,255,0.02)` : `linear-gradient(180deg, ${c}22 0%, ${c}00 100%)`,
                border: isEmpty ? `1px dashed ${c}22` : `1px solid ${c}33`,
                borderRadius: '10px 10px 0 0',
                marginTop: 4,
                display: 'grid', placeItems: 'center',
            }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: isEmpty ? `${c}44` : c, letterSpacing: '-0.02em' }}>{place}</span>
            </div>
        </div>
    );
}

function PlayerRow({ player }) {
    const pct = parseFloat(player.pct) || 0;
    return (
        <div
            className="prof-classement-row"
            style={{
                display: 'grid', gridTemplateColumns: '24px 32px 1fr auto auto', gap: 10, alignItems: 'center',
                borderBottom: '1px solid var(--border)', padding: '10px 0',
            }}
        >
            <span className="num" style={{
                fontSize: 12, fontWeight: 700,
                color: player.rank <= 3 ? 'var(--gold)' : 'var(--text-mute)',
            }}>{player.rank}</span>
            <NameAvatar name={player.username} size={32} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{player.username}</div>
                <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>{player.team_name}</div>
            </div>
            {player.capital != null && (
                <div className="num" style={{ fontSize: 11, fontWeight: 600, textAlign: 'right' }}>
                    {formatPrice(player.capital)}
                </div>
            )}
            <div className="num" style={{
                fontSize: 11, fontWeight: 600,
                color: pct >= 0 ? 'var(--up)' : 'var(--down)',
                minWidth: 48, textAlign: 'right',
            }}>{fmtPct(pct)}</div>
        </div>
    );
}

function TeamRow({ team, rank }) {
    const pct = parseFloat(team.total_montant) || 0;
    return (
        <div
            className="prof-classement-row"
            style={{
                display: 'grid', gridTemplateColumns: '24px 32px 1fr auto', gap: 10, alignItems: 'center',
                borderBottom: '1px solid var(--border)', padding: '10px 0',
            }}
        >
            <span className="num" style={{
                fontSize: 12, fontWeight: 700,
                color: rank <= 3 ? 'var(--gold)' : 'var(--text-mute)',
            }}>{rank}</span>
            <NameAvatar name={team.team_name} size={32} />
            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {team.team_name}
            </div>
            <div className="num" style={{
                fontSize: 12, fontWeight: 600,
                color: pct >= 0 ? 'var(--up)' : 'var(--down)',
                minWidth: 60, textAlign: 'right',
            }}>{fmtPct(pct)}</div>
        </div>
    );
}

export default ClassementProf;
